import { formDataToObject, GestCard, iconColumnSettings, ICoordinates, IMarker, isAdminOrEditor, ISharedConfig, setDialog, setSnackbar } from '@shared/library';
import openGestConfirmationDialog from '@shared/library/dist/components/gest/GestDialog/GestConfirmationDialog/GestConfirmationDialog';
import { languages } from '@shared/library/dist/static/language';
import axios, { AxiosError } from 'axios';
import React from 'react';
import config from '../../../../config';
import importer from '../../../../helpers/importer';
import { editPlanNameAPI, setFreePlanName } from '../methods';
import { planColumns, planHiddenColumns, WFMControllerName, WFMLeftGrid, WFMRightGrid } from '../static';
import './PlanAssignments.scss';
//#region ICONS
import { Block, Check, Delete, Undo } from '@mui/icons-material';
import ArchiveIcon from '@mui/icons-material/Archive';
import ConfirmIcon from '@mui/icons-material/CheckRounded';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteRounded';
import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded';
import AddIcon from '@mui/icons-material/PersonAdd';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import UnarchiveRoundedIcon from '@mui/icons-material/UnarchiveRounded';
//#endregion
//#region COMPONENTS
import GestDataGridPro from '@shared/library/dist/components/gest/GestDataGrid/GestDataGridPro';
import GestButton from '@shared/library/dist/components/gest/GestInputs/button/GestButton';
import GestSaveButton from '@shared/library/dist/components/gest/GestInputs/button/save/GestSaveButton';
import GestSelect from '@shared/library/dist/components/gest/GestInputs/select/GestSelect';
import GestSwitch from '@shared/library/dist/components/gest/GestInputs/switch/GestSwitch';
import GestTextArea from '@shared/library/dist/components/gest/GestInputs/text area/GestTextArea';
import GestMap from '@shared/library/dist/components/gest/GestMap/GestMap';
import Title from '@shared/library/dist/components/gest/Title/Title';
import { Caption } from '../../../_gest';
//#endregion
//#region MODELS
import { GridColDef, GridRowId, GridRowParams } from '@mui/x-data-grid';
import { IGestSelectOption } from '@shared/library/dist/components/gest/GestInputs/select/GestSelectProps';
import { LangKey } from '../../../../models/ui_data/Lang_model';
import { IIntervention } from '../_models/Intervention';
import { IOperator, IOperatorCreate } from '../_models/Operator';
import { IPlan } from '../_models/Plan';
//#endregion

const PlanAssignments = (props: ISharedConfig) => {
    //#region STATIC DATA
    const lang = languages(props.settings?.lang);
    document.title = config.app.name;
    const maxAssignedInterventions = 15_000;
    //#endregion

    //#region STATES
    //PLANS
    const [unarchivedPlans, setUnarchivedPlans] = React.useState<IPlan[]>([]);
    const [archivedPlans, setArchivedPlans] = React.useState<IPlan[]>([]);
    const [clickedPlan, setClickedPlan] = React.useState<IPlan>();

    const unarchivedPlansRef = React.useRef<IPlan[]>([]);
    const planID4ActionsRef = React.useRef<number>(undefined);
    //OPERATORS
    const [operators, setOperators] = React.useState<IOperator[]>([]);
    const [selectedOperator, setSelectedOperator] = React.useState<IOperator>();

    const selectedOperatorRef = React.useRef<IOperator>(undefined);
    const crerateOperatorForm = React.useRef(null);
    //PLANS ASSIGNED TO OPERATORS
    const [addedPlans, setAddedPlans] = React.useState<IPlan[]>([]);
    const [removedPlans, setRemovedPlans] = React.useState<IPlan[]>([]);
    const [operatorPlans, setOperatorPlans] = React.useState<IPlan[]>([]);

    const operatorPlansRef = React.useRef<IPlan[]>([]);
    //MODE
    const [viewMode, setView] = React.useState<'available' | 'archived'>('available');
    //MARKERS
    const [center, setCenter] = React.useState<ICoordinates>();
    //FLAGS
    const [assignmentLoading, setAssignmentLoading] = React.useState(false);
    const [unassignmentLoading, setUnassignmentLoading] = React.useState(false);
    const [operatorsLoading, setOperatorsLoading] = React.useState(false);
    const [deletePlanLoading, setDeletePlanLoading] = React.useState(false);
    const [archiveLoading, setArchiveLoading] = React.useState(false);
    const [plansLoading, setPlansLoading] = React.useState(false);
    //#endregion

    //#region METHODS
    //#region API CALLS

    //#region GET
    const getPlansAPI = (archived?: boolean) => {
        setPlansLoading(true);
        return axios.get<IPlan[]>(`${config.api.internal}/${WFMControllerName}/Plans`, { withCredentials: true, params: { archived, withOperators: true, withInterventions: true } })
            .then(async res => {
                if (archived === undefined) {
                    const plans = res.data;
                    unarchivedPlansRef.current = plans.filter(p => !p.archived_at);
                    const archivedPlans = plans.filter(p => p.archived_at);

                    setUnarchivedPlans(unarchivedPlansRef.current);
                    setArchivedPlans(archivedPlans);
                }
                else if (archived) {
                    setArchivedPlans(res.data);
                }
                else {
                    unarchivedPlansRef.current = res.data;
                    setUnarchivedPlans(unarchivedPlansRef.current);
                }

                if (!archived && selectedOperatorRef.current?.id) {
                    updateSelectedOperator(selectedOperatorRef.current.id as string);
                }
            })
            .catch((err: AxiosError) => setSnackbar(props, { message: err.response?.data as string, severity: 'error' }))
            .finally(() => setPlansLoading(false));
    };

    const getOperatorsAPI = () => {
        setOperatorsLoading(true);
        return axios.get<IOperator[]>(`${config.api.internal}/${WFMControllerName}/Operators`, { withCredentials: true })
            .then(res => {
                setOperators(res.data);

                if (selectedOperator) {
                    const operator = res.data.find(o => o.status && o.id === selectedOperator.id);
                    setSelectedOperator(operator);

                    if (!operator) {
                        setOperatorPlans([]);
                    }
                }
            })
            .catch((err: AxiosError) => setSnackbar(props, { message: err.response?.data as string, severity: 'error' }))
            .finally(() => setOperatorsLoading(false));
    };
    //#endregion

    //#region POST
    const createOperatorAPI = (event: React.SubmitEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (crerateOperatorForm.current) {
            const newItem = formDataToObject<IOperatorCreate>(crerateOperatorForm.current);
            setOperatorsLoading(true);
            return axios.post(`${config.api.internal}/${WFMControllerName}/Operator/Create`, newItem, { withCredentials: true })
                .then(() => {
                    setSnackbar(props, { message: lang.successfully_created, severity: 'success' });
                    getOperatorsAPI();
                    setDialog(props);
                })
                .catch((err: AxiosError) => setSnackbar(props, { message: lang[(err.response!.data as string).toLowerCase() as LangKey], severity: 'error' }))
                .finally(() => setOperatorsLoading(false));
        }
    };

    const assignPlansAPI = () => {
        if (selectedOperator?.id && addedPlans.length && isUnderAssignmentLimit()) {
            //is under limit 
            setAssignmentLoading(true);
            const ids = addedPlans.map(ap => ap.id);
            return axios.post(`${config.api.internal}/${WFMControllerName}/Operator/${selectedOperator.id}/AssignPlans`, { PlanIds: Array.from(ids) }, { withCredentials: true })
                .then(() => {
                    setAddedPlans([]);
                    setSnackbar(props, { message: `Nuovi ${addedPlans.length} piani assegnati con successo`, severity: "success" });
                })
                .catch((err: AxiosError) => {
                    console.error(err);
                    setSnackbar(props, { message: `Errore durante assegnazione dei ${addedPlans.length} piani selezionati`, severity: 'error' });
                })
                .finally(() => setAssignmentLoading(false));
        }
    };
    //#endregion

    //#region PATCH
    const unassignPlansAPI = () => {
        if (selectedOperator?.id && removedPlans.length) {
            setUnassignmentLoading(true);
            const planIDs = removedPlans.map(rp => rp.id);
            return axios.patch(`${config.api.internal}/${WFMControllerName}/Operator/${selectedOperator.id}/UnassignPlans`, planIDs, { withCredentials: true })
                .then(() => {
                    setRemovedPlans([]);
                    setSnackbar(props, { message: `Rimossi ${removedPlans.length} piani da assegnazione con successo`, severity: "success" });
                })
                .catch((err: AxiosError) => {
                    console.error(err);
                    setSnackbar(props, { message: `Errore durante rimozione assegnazione dei ${removedPlans.length} piani selezionati`, severity: 'error' });
                })
                .finally(() => setUnassignmentLoading(false));
        }
    };

    const updatePlanArchiviationAPI = (isToArchive: boolean) => {
        if (planID4ActionsRef.current) {
            setArchiveLoading(true);
            return axios.patch(`${config.api.internal}/${WFMControllerName}/Plan/${planID4ActionsRef.current}/Archiviation/${isToArchive}`, undefined, { withCredentials: true })
                .then(() => getPlansAPI())
                .catch((err) => console.error(err))
                .finally(() => setArchiveLoading(false));
        }
    };

    const updateOperatorStatusAPI = (operator: IOperator) => {
        if (operator.status && operator.id === selectedOperator?.id) {
            resetAssignments();
        }

        setOperatorsLoading(true);
        return axios.patch(`${config.api.internal}/${WFMControllerName}/Operator/${operator.id}/Status/${!operator.status}`, undefined, { withCredentials: true })
            .then(getOperatorsAPI)
            .catch((err: AxiosError) => console.error(err))
            .finally(() => {
                setDialog(props, undefined);
                setOperatorsLoading(false);
            });
    };

    const editPlanName = async (prevPlan: IPlan, newName: string) => {
        setPlansLoading(true);
        return editPlanNameAPI(prevPlan, newName)
            .finally(() => setPlansLoading(false));
    };

    const submitPlanNameEditing = async (curr: IPlan, prev: IPlan) => {
        let edited = false;

        if (curr.name && curr.name !== prev.name) {
            if (prev.name?.toLowerCase().includes("free")) {
                curr.name = setFreePlanName(curr.name);
            }

            if (curr.operators?.length) {//assigned
                //ask to confirm
                const confirmed = await openGestConfirmationDialog(dialog => setDialog(props, dialog), lang, "Questo piano è stato assegnato a uno o più operatori");
                edited = Boolean(confirmed && await editPlanName(prev, curr.name!));
            }
            else {
                edited = Boolean(await editPlanName(prev, curr.name!));
            }
        }

        return edited ? curr : prev;
    };
    //#endregion

    //DELETE
    const deletePlanAPI = (archived: boolean, planID: number) => {
        if (planID) {
            setDeletePlanLoading(true);
            return axios.delete(`${config.api.internal}/${WFMControllerName}/Plan/${planID}`, { withCredentials: true })
                .then(() => getPlansAPI(archived))
                .catch((err: AxiosError) => console.error(err))
                .finally(() => setDeletePlanLoading(false));
        }
    };
    //#endregion

    const isUnderAssignmentLimit = () => {
        let canProceed = true;

        let count = 0;
        operatorPlans.forEach(p => count += (p.interventionsCount ?? p.interventions?.length ?? 0));

        if (count >= maxAssignedInterventions) {
            setSnackbar(props, { message: `Impossibile assegnare più di ${maxAssignedInterventions} piani ad un operatore`, severity: "error" });
            canProceed = false;
        }

        return canProceed;
    };

    const updateSelectedOperator = (operatorID: string) => {
        resetAssignments();
        const operator = operators.find(o => o.id === operatorID);

        if (operator) {
            selectedOperatorRef.current = operator;
            setSelectedOperator(selectedOperatorRef.current);

            operatorPlansRef.current = unarchivedPlansRef.current.filter(p => p.operators?.includes(selectedOperatorRef.current!.id as string));
            setOperatorPlans(operatorPlansRef.current);
        }
    };

    const renderMarkerIcon = (intervention: IIntervention) => {
        let icon = importer.ic.require('markerGray.png');

        switch (intervention.status) {
            case "processed":
                icon = importer.ic.require('markerGreen.png');
                break;
            case "unprocessed":
                icon = importer.ic.require('markerBlue.png');
                break;
            case "attempted_processing":
                icon = importer.ic.require('markerRed.png');
                break;
            case "suspended":
                icon = importer.ic.require('markerBanana.png');
                break;
        }

        return icon;
    };

    const updatePlansSelection = (ids: GridRowId[]) => {
        const lastAdded = unarchivedPlans.filter(p => ids.includes(p.id as number) && !operatorPlans.some(op => op.id === p.id));
        const notAssignable = lastAdded.filter(p => p.name?.toLowerCase().includes("free") && p?.operators?.length);

        if (notAssignable.length) {
            notAssignable.forEach(p => setSnackbar(props, { severity: "error", message: `Piano ${p.name} già assegnato. Rimuovere assegnazione dai precedenti operatori per assegnarlo nuovamente` }));
            return;
        }

        const removed = unarchivedPlans.filter(p =>
            !ids.includes(p.id as number) &&
            operatorPlans.some(op => op.id === p.id && op.operators?.includes(selectedOperator!.id as string))
        );
        const prevAdded = addedPlans.filter(ap => ids.includes(ap.id as number) && !removed.some(rp => rp.id === ap.id));

        const added = [...prevAdded, ...lastAdded];

        const remaining = operatorPlans.filter(p =>
            !added.some(ap => ap.id === p.id) &&
            !removed.some(ap => ap.id === p.id) && p.operators?.includes(selectedOperator!.id as string)
        );

        setRemovedPlans(removed);
        setAddedPlans(added);
        setOperatorPlans([...added, ...removed, ...remaining]);
    };

    const resetAssignments = () => {
        setAddedPlans([]);
        setRemovedPlans([]);
        const remaining = operatorPlans.filter(p => p.operators?.includes(selectedOperator!.id as string));
        setOperatorPlans(remaining);
    };

    const clearAssignments = () => {
        setAddedPlans([]);
        const remaining = operatorPlans.filter(o => !addedPlans.some(ap => ap.id === o.id));
        setRemovedPlans(remaining);
        setOperatorPlans(remaining);
    };

    //#region DIALOGS
    const openCreateOperatorDialog = () => setDialog(props, {
        label: `${lang.create_new} ${lang.operator}`,
        content:
            <form
                ref={crerateOperatorForm}
                onSubmit={createOperatorAPI}
            >
                <GestTextArea name="firstname" label={lang.firstname} fullw />
                <GestTextArea name="lastname" label={lang.lastname} fullw />
                <GestTextArea name="username" label="Username" alphaNumeric required fullw />
                <GestTextArea name="password" type='password' label="Password" required fullw />
                <GestSwitch name="status" checked={true} label="Attivo" />

                <GestSaveButton label={lang.save} loading={operatorsLoading} submit fullw />
            </form>
    });

    const openSaveAssignmentsDialog = () => {
        if (selectedOperator?.id) {
            const remaining = operatorPlans
                .filter(p => p.operators?.includes(selectedOperator.id as string))
                .sort((a, b) => (a.id as number) - (b.id as number));
            const commonGridProps = {
                rows: remaining,
                columns: planColumns({ lang, excluded: ["archived_at"] }),
                w: "30vw",
                h: "calc(100% - 15px)"
            };

            setDialog(props, {
                h: "65vh",
                label: `Nuova assegnazione operatore ${selectedOperator.username}`,
                content:
                    <div className='flex'>
                        <div>
                            <Title>Prima</Title>
                            <GestDataGridPro
                                {...commonGridProps}
                                getRowClassName={params => removedPlans.some(rp => rp.id === params.row.id) ? 'draftRemove' : ""}
                            />
                        </div>

                        <div>
                            <Title>Dopo: {addedPlans.length ? <span className='text-success'>Assegnati {addedPlans.length}</span> : ""} {removedPlans.length ? <span className='text-danger'>Rimossi {removedPlans.length}</span> : ""}</Title>
                            <GestDataGridPro
                                {...commonGridProps}
                                rows={[...addedPlans, ...remaining.filter(p => !removedPlans.some(rp => rp.id === p.id))]}
                                getRowClassName={params => addedPlans.some(p => p.id === params.row.id) ? 'draftAdd' : ""}
                            />
                        </div>
                    </div>,
                footer:
                    <GestSaveButton
                        label={lang.save}
                        loading={assignmentLoading || unassignmentLoading}
                        onclick={() => Promise.all([assignPlansAPI(), unassignPlansAPI()])
                            .finally(() => {
                                getPlansAPI(false);
                                setDialog(props, undefined);
                            })
                        }
                    />
            });
        }
    };

    const openOperatorStatusChangeDialog = () => setDialog(props, {
        label: 'Attenzione',
        content: `Le modifiche all'assegnazione dell'operatore ${selectedOperator?.username ?? ""} andranno perse. Continuare?`,
        footer:
            <GestButton
                label={lang.confirm}
                onclick={() => updateOperatorStatusAPI(selectedOperator!)}
                loading={operatorsLoading}
                icon={<ConfirmIcon />}
            />
        ,
    });

    const openDeletePlanDialog = () => setDialog(props, {
        label: lang.warning,
        content:
            <>
                <Title>{lang.are_you_sure_wanna_delete_plan}</Title>
                <p className='text-justify'>{lang.deleting_plan_info}</p>
                <Caption variant="warning" size="lg">{lang.proceeding_operation_misalignments_exports}</Caption>
            </>,
        footer:
            <GestButton
                color='error'
                h={40}
                label={lang.delete}
                loading={deletePlanLoading}
                icon={<DeleteForeverRoundedIcon />}
                onclick={() => {
                    if (planID4ActionsRef.current) {
                        setDialog(props, undefined);
                        deletePlanAPI(viewMode === "archived", planID4ActionsRef.current);
                    }
                }}
            />
        ,
    });

    const openPlanArchiviationChangeDialog = (mode?: "archive") => setDialog(props, {
        label: lang.warning,
        content:
            <div className="flexColumnContainer">
                <Title>{mode === "archive" ? lang.are_you_sure_wanna_archive_plan : lang.are_you_sure_wanna_restore_plan}</Title>

                {mode === "archive" && <>
                    <p className='text-justify'>{lang.archiving_plan_info}</p>
                    <Caption variant="star" size="lg">{lang.proceeding_operation_misalignments_exports}</Caption>
                </>}
            </div>,
        footer:
            <GestButton
                h={40}
                label={mode === "archive" ? lang.store : lang.restore}
                loading={archiveLoading}
                icon={mode === "archive" ? <ArchiveIcon /> : <UnarchiveRoundedIcon />}
                onclick={() => {
                    setDialog(props, undefined);
                    updatePlanArchiviationAPI(mode === "archive");
                }}
            />
        ,
    });
    //#endregion

    //#endregion

    //STATES CHANGE
    React.useEffect(() => {
        getPlansAPI();
        getOperatorsAPI();
    }, []);

    //RENDER
    const plansColumns = React.useMemo<GridColDef<IPlan>[]>(() => [
        iconColumnSettings(
            "removal"
            , <DeleteForeverRoundedIcon color="error" />
            , `${lang.delete} ${lang.plan}`
            , (params) => {
                planID4ActionsRef.current = params.row.id as number;
                openDeletePlanDialog();
            }
        ),
        iconColumnSettings(
            'archiviation'
            , viewMode === "available" ? <ArchiveIcon /> : <UnarchiveRoundedIcon />
            , viewMode === "available" ? lang.store : lang.restore
            , (params) => {
                planID4ActionsRef.current = params.row.id as number;
                openPlanArchiviationChangeDialog(viewMode === "available" ? "archive" : undefined);
            }
        ),
        ...planColumns({ lang, operators }),
    ], [lang, viewMode, selectedOperator, operatorPlans]);

    return <div id="planAssignments">
        <Title size="xl">{lang.plan_assignment}</Title>

        <GestCard id="plans"
            title={viewMode === 'available' ? lang.plans : lang.stored_plans}
        >
            <GestDataGridPro
                loading={plansLoading || unassignmentLoading || assignmentLoading}
                columns={plansColumns}
                rows={viewMode === 'available' ? unarchivedPlans : archivedPlans}

                processRowUpdate={submitPlanNameEditing}
                updateSelection={viewMode === 'available' && selectedOperator?.status ? updatePlansSelection : undefined}
                selectedRowIDs={viewMode === 'available' ? [...operatorPlans.map(p => p.id as number), ...addedPlans.map(p => p.id as number)].filter(id => !removedPlans.some(rp => rp.id === id)) : undefined}
                onRowClick={(params: GridRowParams<IPlan>) => {
                    setClickedPlan(params.row);
                    const firstWithCoords = params.row.interventions?.find(i => i.lat && i.lng);

                    if (firstWithCoords) {
                        setCenter(firstWithCoords as ICoordinates);
                    }
                }}

                columnVisibilityModel={{ ...planHiddenColumns, action: isAdminOrEditor(props.session?.userData?.role) && viewMode === "available" && Boolean(selectedOperator?.status), archived_at: viewMode !== "available" }}
                toolbar={[
                    {
                        tooltip: `${lang.plans} ${viewMode === 'available' ? lang.stored_s : lang.availables}`,
                        icon: viewMode === 'available' ? <ArchiveIcon /> : <FormatListBulletedRoundedIcon />,
                        onclick: () => setView(viewMode === 'available' ? "archived" : 'available')
                    },
                    {
                        tooltip: "Refresh",
                        icon: <RefreshRoundedIcon />,
                        onclick: () => getPlansAPI(viewMode !== 'available')
                    }
                ]}

                w={WFMLeftGrid}
            />
        </GestCard>

        <GestCard id="operatorPlans"
            title={`Piani Assegnati all'Operatore ${selectedOperator?.username ?? ""}`}
            actions={
                <div className="align-items-center">
                    <GestSelect
                        w={250}
                        required
                        label={lang.operator}
                        value={selectedOperator?.id}
                        onchange={selected => updateSelectedOperator(selected[0].value as string)}
                        options={operators.map<IGestSelectOption<undefined>>(o => ({
                            value: o.id,
                            label: o.username,
                            style: !o.status ? { opacity: "0.5" } : undefined,
                            labelAdditional:
                                <GestButton
                                    iconBtn
                                    loading={operatorsLoading}
                                    tooltip={o.status ? lang.disable : lang.enable}
                                    icon={o.status ? <Block color='error' /> : <Check color='success' />}
                                    onclick={() => {
                                        if (o.status && (addedPlans.length || removedPlans.length)) {
                                            openOperatorStatusChangeDialog();
                                        }
                                        else {
                                            updateOperatorStatusAPI(o);
                                        }
                                    }}
                                />
                        }))}
                    />

                    <GestButton
                        iconBtn
                        tooltip={`${lang.create_new} ${lang.operator}`}
                        onclick={openCreateOperatorDialog}
                        icon={<AddIcon />}
                    />
                </div>
            }
        >
            <GestDataGridPro
                columns={planColumns({ lang, operators, excluded: ["archived_at"] })}
                rows={operatorPlans}

                w={WFMRightGrid}
                loading={plansLoading || unassignmentLoading || assignmentLoading}
                getRowClassName={params => addedPlans.some(p => p.id === params.row.id) ? 'draftAdd' : removedPlans.some(p => p.id === params.row.id) ? 'draftRemove' : ""}
                toolbar={[
                    {
                        icon: <Delete />,
                        tooltip: "Rimuovi tutte le assegnazioni",
                        onclick: clearAssignments,
                        disabled: !selectedOperator || !operatorPlans.length
                    },
                    {
                        icon: <Undo />,
                        tooltip: lang.cancel,
                        onclick: resetAssignments,
                        disabled: !(selectedOperator && (addedPlans.length || removedPlans.length))
                    },
                    {
                        icon: <SaveRoundedIcon />,
                        tooltip: lang.preview,
                        onclick: openSaveAssignmentsDialog,
                        disabled: !(selectedOperator && (addedPlans.length || removedPlans.length))
                    },
                ]}
            />
        </GestCard>

        <GestCard id="map">
            <GestMap
                center={center || config.env.map.defaultCenter}
                markers={clickedPlan?.interventions?.map<IMarker<IIntervention>>(i => ({
                    id: i.id!,
                    model: i,
                    options: {
                        icon: renderMarkerIcon(i),
                        position: i as ICoordinates,
                    }
                }))
                }
            />
        </GestCard>
    </div >;
};
export default React.memo(PlanAssignments);